import React, { useState, useRef, useEffect, useContext, useMemo } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import { ErrorPopup } from "../error/ErrorPopup";
import styles from "../../styles/components/UI/DropdownSelector.module.css"; 
import { LangContext } from '../../context/LangContext';
import { ThemeContext } from '../../context/ThemeContext';

const translations = {
    ru: {
        placeholder: "Выберите...",
        selected: (count) => `Выбрано: ${count}`,
        noData: "Данных нет",
    },
    en: {
        placeholder: "Select...",
        selected: (count) => `Selected: ${count}`,
        noData: "No data available",
    },
};

export const MultiSelectDropdown = ({
    filterName,
    filterKey,
    selectedValue = [], 
    handleFilterChange,
    options = [],
    placeholder,
    dropdownAlign = 'left',
    shouldAutoClearOnMissingOptions = true,
    isSearchable = false,
    closeOnSelect = true,
    searchPlaceholder,
    hasError,
    errorMessage,
    onFocus,
    onBlur,
}) => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const t = translations[langSelected] || translations.ru;
    const isDark = theme === 'dark';

    const [isOpen, setIsOpen] = useState(false);
    const selectRef = useRef(null);
    const searchInputRef = useRef(null);
    const [isMouseEnter, setIsMouseEnter] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const hasOptions = options.length > 0;

    const getOptionValue = (option) => {
        return typeof option === 'object' && option !== null && option.hasOwnProperty('value') ? option.value : option;
    };
    
    const getOptionLabel = (option) => {
        return typeof option === 'object' && option !== null && option.hasOwnProperty('label') ? option.label : option;
    };

    const getOptionDescription = (option) => {
        return typeof option === 'object' && option !== null && option.hasOwnProperty('description') ? option.description : '';
    };

    const getOptionIconPath = (option) => {
        return typeof option === 'object' && option !== null && option.hasOwnProperty('iconPath') ? option.iconPath : '';
    };

    const handleMouseEnter = () => {
        setIsMouseEnter(true);
    };

    const handleMouseLeave = () => {
        setIsMouseEnter(false);
    };

    const openDropdown = () => {
        if (!hasOptions) {
            return;
        }

        if (isSearchable) {
            setSearchTerm('');
        }

        setIsOpen(true);
        if (onFocus) onFocus(filterName);
    };

    const closeDropdown = () => {
        setIsOpen(false);
        if (onBlur) onBlur(filterName);
    };

    const toggleDropdown = () => {
        if (hasOptions) {
            if (isOpen) {
                closeDropdown();
                return;
            }

            openDropdown();
        }
    };

    const handleSearchableFieldClick = () => {
        if (!hasOptions) {
            return;
        }

        openDropdown();
        searchInputRef.current?.focus();
    };

    const handleOptionClick = (option) => {
        const value = getOptionValue(option);
        
        let newSelectedValue;
        
        if (selectedValue.includes(value)) {
            newSelectedValue = selectedValue.filter(v => v !== value);
        } else {
            newSelectedValue = [...selectedValue, value];
        }
        
        handleFilterChange(filterName, newSelectedValue, filterKey);

        if (isSearchable && closeOnSelect) {
            const selectedLabel = getOptionLabel(option);
            setSearchTerm(typeof selectedLabel === 'string' ? selectedLabel : '');
            closeDropdown();
        }
    };

    const handleSearchChange = (event) => {
        if (!isOpen) {
            openDropdown();
        }

        setSearchTerm(event.target.value);
    };
    
    const isSelected = (value) => {
        return selectedValue.includes(value);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        handleFilterChange(filterName, [], filterKey); 
        setSearchTerm('');
        if (onBlur) onBlur(filterName);
    };

    const getCurrentSelectedLabel = useMemo(() => {
        if (!hasOptions) {
            return <span className={`${styles.selectPlaceholder} ${styles.noDataPlaceholder} ${isDark ? styles.darkPlaceholder : ''}`}>{t.noData}</span>;
        }

        if (selectedValue.length === 0) {
            return <span className={`${styles.selectPlaceholder} ${isDark ? styles.darkPlaceholder : ''}`}>{placeholder || t.placeholder}</span>;
        }
        
        const selectedOptionsLabels = options
            .filter(opt => selectedValue.includes(getOptionValue(opt)))
            .map(opt => getOptionLabel(opt));

        if (selectedOptionsLabels.length === 1) {
            return options.find(opt => selectedValue.includes(getOptionValue(opt))) || selectedOptionsLabels[0];
        }
        
        return t.selected(selectedOptionsLabels.length);
    }, [selectedValue, options, t, hasOptions, placeholder]);

    const selectedDisplayText = useMemo(() => {
        if (!hasOptions || selectedValue.length === 0) {
            return '';
        }

        const selectedOptionsLabels = options
            .filter(opt => selectedValue.includes(getOptionValue(opt)))
            .map(opt => getOptionLabel(opt))
            .filter(Boolean);

        if (selectedOptionsLabels.length === 0) {
            return '';
        }

        if (selectedOptionsLabels.length === 1) {
            return String(selectedOptionsLabels[0]);
        }

        return t.selected(selectedOptionsLabels.length);
    }, [selectedValue, options, t, hasOptions]);

    const renderOptionContent = (option) => (
        <div className={styles.optionContent}>
            {getOptionIconPath(option) && (
                <img
                    src={getOptionIconPath(option)}
                    alt=""
                    className={styles.optionIcon}
                    loading="lazy"
                    aria-hidden="true"
                />
            )}
            <span className={`${styles.optionLabel} ${isDark ? styles.darkText : ''}`}>{getOptionLabel(option)}</span>
        </div>
    );

    const shouldRenderOptionContent = getCurrentSelectedLabel
        && typeof getCurrentSelectedLabel === 'object'
        && !React.isValidElement(getCurrentSelectedLabel)
        && (Object.prototype.hasOwnProperty.call(getCurrentSelectedLabel, 'value') || Object.prototype.hasOwnProperty.call(getCurrentSelectedLabel, 'label'));

    const filteredOptions = useMemo(() => {
        if (!isSearchable) {
            return options;
        }

        const normalizedSearchTerm = searchTerm.trim().toLowerCase();

        if (!normalizedSearchTerm) {
            return options;
        }

        return options.filter((option) => {
            const label = String(getOptionLabel(option) || '').toLowerCase();
            const description = String(getOptionDescription(option) || '').toLowerCase();

            return label.includes(normalizedSearchTerm) || description.includes(normalizedSearchTerm);
        });
    }, [getOptionDescription, getOptionLabel, isSearchable, options, searchTerm]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (selectRef.current && !selectRef.current.contains(event.target)) {
                closeDropdown();
                setSearchTerm('');
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [filterName, onBlur, selectRef, hasOptions]);
    
    useEffect(() => {
        if (shouldAutoClearOnMissingOptions && !hasOptions && selectedValue.length > 0) {
            handleFilterChange(filterName, [], filterKey);
        }
    }, [hasOptions, selectedValue, filterName, filterKey, handleFilterChange, shouldAutoClearOnMissingOptions]);


    return (
        <div 
            className={`
                ${styles.selectContainer} 
                ${hasError ? styles.errorContainer : ""}
                ${!hasOptions ? styles.disabledContainer : ""} 
                ${isDark ? styles.darkContainer : ""}
            `}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div 
                className={`
                    ${styles.selectField} 
                    ${hasError ? styles.errorBorder : ""}
                    ${!hasOptions ? styles.noDataBorder : ""}
                    ${isDark ? styles.darkSelectField : ""}
                `}
                onClick={isSearchable ? handleSearchableFieldClick : toggleDropdown}
            >
                {isSearchable ? (
                    <input
                        ref={searchInputRef}
                        type="text"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onFocus={openDropdown}
                        placeholder={searchPlaceholder || selectedDisplayText || placeholder || t.placeholder}
                        className={`${styles.mainSearchInput} ${isDark ? styles.darkMainSearchInput : ''}`}
                    />
                ) : (
                    <span className={`${styles.selectSelectedValue} ${isDark ? styles.darkText : ''}`}>
                        {shouldRenderOptionContent
                            ? renderOptionContent(getCurrentSelectedLabel)
                            : getCurrentSelectedLabel}
                    </span>
                )}
                <div className={styles.selectIconContainer}>
                    {selectedValue.length > 0 && hasOptions && (
                        <div className={styles.selectClearIcon} onClick={handleClear}>
                            <CloseIcon fontSize="inherit" />
                        </div>
                    )}
                    <div className={styles.selectToggleIcon} onClick={(event) => {
                        event.stopPropagation();
                        toggleDropdown();
                    }}>
                        {hasOptions && <KeyboardArrowDownIcon fontSize="inherit" />}
                    </div>
                </div>
            </div>
            {isOpen && hasOptions && (
                <ul
                    className={`${styles.optionsList} ${dropdownAlign === 'right' ? styles.optionsListRight : ''} ${isDark ? styles.darkOptionsList : ''}`}
                    ref={selectRef}
                >
                    {filteredOptions.map((option, index) => {
                        const value = getOptionValue(option);
                        const isOptSelected = isSelected(value);
                        return (
                            <li
                                key={index}
                                className={`${styles.optionItem} ${isOptSelected ? styles.selected : ""} ${isDark ? styles.darkOptionItem : ""} ${isOptSelected && isDark ? styles.darkSelected : ""}`}
                                onClick={() => handleOptionClick(option)}
                            >
                                {renderOptionContent(option)}
                                {getOptionDescription(option) && (
                                    <div className={`${styles.optionDescription} ${isDark ? styles.darkDesc : ''}`}>({getOptionDescription(option)})</div>
                                )}
                            </li>
                        );
                    })}
                    {filteredOptions.length === 0 && (
                        <li className={`${styles.emptyState} ${isDark ? styles.darkEmptyState : ''}`}>
                            {t.noData}
                        </li>
                    )}
                </ul>
            )}
            {hasError && isMouseEnter && errorMessage && <ErrorPopup message={errorMessage} theme={theme} />}
        </div>
    );
};
